import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OperatorAdminService } from '../../../services/operator-admin/operator-admin.service';
import { AdvanceSearchService } from '../../../services/Advance-search/advance-search.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';
import { FamilyMemberLookup } from '../../../common-files/family-member-lookup';
import { UsernameAvailability } from '../../../common-files/username-availability';

// add or edit a central operator. identity is SSO-owned: on add the person is
// picked by family code + name and the SSO uuid (memUuid) is mapped from the
// pick; username + memUuid stay fixed on edit. the role list comes from the
// backend (only CENTRAL roles below the admin's weight are offered). self-saving.
@Component({
  selector: 'app-oa-add-operator-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, LoaderComponent],
  templateUrl: './oa-add-operator-dialog.component.html',
  styleUrls: ['./oa-add-operator-dialog.component.scss', '../../../../common-style/dropdown.scss', '../../../../common-style/ac-dialog.scss'],
})
export class OaAddOperatorDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isLoading: boolean = false;
  isEdit: boolean = false;
  roles: { roleKey: string; roleName: string }[] = [];
  // family-member picker (add mode: fills fullName + memUuid)
  readonly memberLookup: FamilyMemberLookup;
  // live username check (add mode, debounced — fires when the admin pauses typing)
  readonly usernameCheck: UsernameAvailability;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<OaAddOperatorDialogComponent>,
    private operatorAdminService: OperatorAdminService,
    private advanceSearchService: AdvanceSearchService,
    private toastService: ToastService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { operator?: any } | null
  ) {
    this.memberLookup = new FamilyMemberLookup(
      (fc) => this.advanceSearchService.fetchPhilDetails(fc),
      (memUuid) => this.advanceSearchService.checkMemberAvailability(memUuid)
    );
    this.usernameCheck = new UsernameAvailability((name) =>
      this.advanceSearchService.checkUsernameAvailability(name)
    );
    this.isEdit = !!this.data?.operator;
    const op = this.data?.operator;
    this.form = this.fb.group({
      userName: [
        { value: op?.userName || '', disabled: this.isEdit },
        [Validators.required, Validators.minLength(5)],
      ],
      fullName: [op?.fullName || '', Validators.required],
      memUuid: [{ value: op?.memUuid || '', disabled: this.isEdit }, this.isEdit ? [] : Validators.required],
      // on edit the role is optional: leave it empty to keep the current role.
      roleKey: ['', this.isEdit ? [] : Validators.required],
      familyCode: [
        op?.familyCode || '',
        this.isEdit ? [] : [Validators.required, Validators.pattern(/^\d{12}$/)],
      ],
      mobileNumber: [op?.mobileNumber || ''],
      emailId: [op?.emailId || ''],
      identityNumber: [op?.identityNumber || ''],
    });
    if (!this.isEdit) {
      this.usernameCheck.attach(this.form.get('userName')!);
      this.memberLookup.attach(
        this.form.get('familyCode')!,
        (message) => this.toastService.error(message),
        () => {}
      );
    }
  }

  ngOnDestroy(): void {
    this.usernameCheck.destroy();
    this.memberLookup.destroy();
  }

  // a changed family code invalidates the picked member (add mode)
  onFamilyCodeInput(): void {
    if (this.isEdit) return;
    this.memberLookup.reset();
    this.form.patchValue({ fullName: '', memUuid: '' });
  }

  // picking a name maps the SSO uuid internally and validates the person at
  // once — someone who already has an account is refused right here.
  onMemberPick(memUuid: string): void {
    this.form.patchValue({ fullName: this.memberLookup.fullNameOf(memUuid) });
    this.memberLookup.verifyPick(memUuid);
  }

  ngOnInit(): void {
    this.operatorAdminService.listAssignableRoles().subscribe({
      next: (res: any) => {
        this.roles = res?.data?.roles || [];
      },
      error: (err) => {
        this.toastService.error(err?.error?.message || 'Failed to load roles.');
      },
    });
  }

  get title(): string {
    return this.isEdit ? 'Edit operator' : 'Add operator';
  }

  submit(): void {
    if (this.form.invalid) {
      this.toastService.error(
        this.isEdit
          ? 'Full name is required.'
          : 'Username (5+ characters), family code, name and role are required.'
      );
      return;
    }
    if (!this.isEdit && this.usernameCheck.status === 'taken') {
      this.toastService.error('This username is already taken.');
      return;
    }
    if (!this.isEdit && this.memberLookup.pickStatus === 'taken') {
      this.toastService.error('This person already has a user account.');
      return;
    }
    this.isLoading = true;

    if (this.isEdit) {
      const value = this.form.value;
      const payload = {
        fullName: value.fullName,
        familyCode: value.familyCode || null,
        mobileNumber: value.mobileNumber || null,
        emailId: value.emailId || null,
        identityNumber: value.identityNumber || null,
        roleKey: value.roleKey || null,
      };
      this.operatorAdminService.updateOperator(this.data!.operator.sumId, payload).subscribe({
        next: (res: any) => {
          this.toastService.success(res?.message || 'Operator updated.');
          this.isLoading = false;
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.isLoading = false;
          this.toastService.error(err?.error?.message || 'Failed to update operator.');
        },
      });
      return;
    }

    this.operatorAdminService.addOperator(this.form.value).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Operator added.');
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to add operator.');
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
